import { useCallback, useMemo } from 'react';
import {
  getAbsolutePlayerTime,
  getPlayerTimeFromAbsoluteTime,
  getPreviousSegmentAbsoluteTime,
  getSegmentAtCurrentFrame
} from '../timeUtils';
import { CHAPTER_TIME_BUFFER } from '../constants';
import { getAllCompositeSegments, getAllSegments } from '../clipUpdateDataUtilsWithSplits/compositeUtils';
import { useTranscriptContext } from '@/context/TranscriptContext/TranscriptContext';
import { useRemotionPlayerCurrentTime } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerCurrentTime';
import { useRemotionPlayerSeek } from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerSeek';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import { isFullLengthClip, getClipFPS } from '@/libs/clipUtils';
import { isSplitsInTimelineEnabled } from '@/stores/clip';
import { SegmentType } from '@/App/remotion/MainPlayer/types/Segment/Type';

const PREV_SEGMENT_THRESHOLD_FACTOR = 2;
const NEXT_SEGMENT_THRESHOLD_FACTOR = 5;

// This hook has a direct subscription to currentTime so always try to use it with smaller components.
export const useChapterSegmentNavigation = () => {
  const transcriptStore = useTranscriptContext();
  const chapters = useMemo(() => transcriptStore.chapterData, [transcriptStore.chapterData]);
  const { currentTime } = useRemotionPlayerCurrentTime();
  const { seekTo } = useRemotionPlayerSeek();
  const { clipData } = useClipsContext();

  const currentSegment = getSegmentAtCurrentFrame(currentTime, clipData);
  const isCurrentSegmentComposite = currentSegment?.type === SegmentType.COMPOSITE;
  const compositeSegment = getAllCompositeSegments(clipData)[0];

  const splitsEnabled = useMemo(() => isSplitsInTimelineEnabled(clipData), [clipData]);
  const fps = useMemo(() => getClipFPS(clipData), [clipData]);
  const isFullClip = useMemo(() => isFullLengthClip(clipData), [clipData]);

  const segmentTimes = useMemo(() => {
    const segments = getAllSegments(clipData);
    const times = segments.map(s => s.fromFrame / fps);
    if (segments.length > 0) {
      times.push(segments[segments.length - 1].toFrame / fps);
    }
    return times;
  }, [clipData, fps]);

  const currentIndex = useMemo(() => {
    let closestIndex = 0;
    let minDiff = Math.abs(currentTime - segmentTimes[0]);
    for (let i = 1; i < segmentTimes.length; i++) {
      const diff = Math.abs(currentTime - segmentTimes[i]);
      if (diff < minDiff) {
        minDiff = diff;
        closestIndex = i;
      }
    }
    return closestIndex;
  }, [currentTime, segmentTimes]);

  const currentChapterIndex = useMemo(() => {
    if (!chapters || !isCurrentSegmentComposite) {
      return;
    }
    const compositeOffsetTime = getPreviousSegmentAbsoluteTime(currentSegment, clipData);
    const trimOffset = currentSegment.currentBounds.fromFrame / fps;
    const segmentAbsoluteTime = getAbsolutePlayerTime(currentTime, clipData) - compositeOffsetTime + trimOffset;

    return chapters.findIndex((chapter, index) => {
      return (
        chapter.start_time - CHAPTER_TIME_BUFFER <= segmentAbsoluteTime &&
        (!chapters[index + 1] || chapters[index + 1].start_time - CHAPTER_TIME_BUFFER > segmentAbsoluteTime)
      );
    });
  }, [chapters, isCurrentSegmentComposite, currentSegment, clipData, fps, currentTime]);

  const nextChapter = useMemo(() => {
    if (!chapters || !compositeSegment) {
      return;
    }
    const previousSegmentAbsoluteTime = getPreviousSegmentAbsoluteTime(compositeSegment, clipData);
    const trimOffset = compositeSegment.currentBounds.fromFrame / fps;
    const segmentAbsoluteTime = getAbsolutePlayerTime(currentTime, clipData) - previousSegmentAbsoluteTime + trimOffset;

    if (compositeSegment.fromFrame / fps > currentTime) {
      return;
    }

    const chapterIndex = chapters.findIndex((chapter, index) => {
      return (
        chapter.start_time - CHAPTER_TIME_BUFFER <= segmentAbsoluteTime &&
        (!chapters[index + 1] || chapters[index + 1].start_time - CHAPTER_TIME_BUFFER > segmentAbsoluteTime)
      );
    });

    return chapters[chapterIndex + 1];
  }, [chapters, clipData, compositeSegment, currentTime, fps]);

  const moveToNextChapter = useCallback(() => {
    if (!chapters || !compositeSegment) {
      return;
    }

    const previousSegmentAbsoluteTime = getPreviousSegmentAbsoluteTime(compositeSegment, clipData);
    const trimOffset = compositeSegment.currentBounds.fromFrame / fps;

    const isNextChapterVisible =
      nextChapter && compositeSegment.currentBounds.toFrame / fps - nextChapter.start_time > 0;
    if (isNextChapterVisible) {
      const nextChapterStartTime = nextChapter.start_time;
      const nextChapterEndTime = getPlayerTimeFromAbsoluteTime(
        nextChapterStartTime + previousSegmentAbsoluteTime - trimOffset + CHAPTER_TIME_BUFFER,
        clipData
      );
      seekTo(nextChapterEndTime + CHAPTER_TIME_BUFFER);
    }
  }, [chapters, clipData, compositeSegment, nextChapter, seekTo, fps]);

  const moveToPreviousChapter = useCallback(() => {
    if (!chapters || !isCurrentSegmentComposite || !currentChapterIndex) {
      return;
    }
    const compositeOffsetTime = getPreviousSegmentAbsoluteTime(currentSegment, clipData);
    const trimOffset = currentSegment.currentBounds.fromFrame / fps;

    const previousChapter = chapters[currentChapterIndex - 1];

    const isPreviousChapterVisible = previousChapter && chapters[currentChapterIndex].start_time - trimOffset > 0;
    if (isPreviousChapterVisible) {
      const chapterStartTime = previousChapter.start_time - trimOffset;
      const newStartTime = getPlayerTimeFromAbsoluteTime(
        compositeOffsetTime + (chapterStartTime > 0 ? chapterStartTime : 0) + CHAPTER_TIME_BUFFER,
        clipData
      );

      if (newStartTime < 0.1) {
        seekTo(0);
      } else if (Math.abs(currentTime - newStartTime) < 0.5) {
        seekTo(newStartTime - 0.5);
      } else {
        seekTo(newStartTime > 0 ? newStartTime + CHAPTER_TIME_BUFFER : 0);
      }
    }
  }, [chapters, isCurrentSegmentComposite, currentSegment, clipData, currentChapterIndex, currentTime, seekTo, fps]);

  const moveToNextSegment = useCallback(() => {
    if (currentIndex < segmentTimes.length - 1) {
      let targetIndex = currentIndex + 1;
      const threshold = NEXT_SEGMENT_THRESHOLD_FACTOR / fps;
      if (Math.abs(segmentTimes[targetIndex] - currentTime) < threshold && targetIndex < segmentTimes.length - 1) {
        targetIndex++;
      }
      seekTo(segmentTimes[targetIndex]);
    }
  }, [currentIndex, segmentTimes, currentTime, seekTo, fps]);

  const moveToPrevSegment = useCallback(() => {
    if (currentIndex > 0) {
      let targetIndex = currentIndex - 1;
      const threshold = PREV_SEGMENT_THRESHOLD_FACTOR / fps;
      if (Math.abs(segmentTimes[targetIndex] - currentTime) < threshold && targetIndex > 0) {
        targetIndex--;
      }
      seekTo(segmentTimes[targetIndex]);
    }
  }, [currentIndex, segmentTimes, currentTime, seekTo, fps]);

  const onClickNext = useCallback(() => {
    if (isFullClip && !splitsEnabled) {
      moveToNextChapter();
    } else {
      moveToNextSegment();
    }
  }, [moveToNextChapter, isFullClip, splitsEnabled, moveToNextSegment]);

  const onClickPrev = useCallback(() => {
    if (isFullClip && !splitsEnabled) {
      moveToPreviousChapter();
    } else {
      moveToPrevSegment();
    }
  }, [moveToPreviousChapter, isFullClip, splitsEnabled, moveToPrevSegment]);

  const isNextDisabled = useMemo(() => {
    if (isFullClip && !splitsEnabled) {
      return !nextChapter;
    }
    return currentIndex >= segmentTimes.length - 1;
  }, [isFullClip, splitsEnabled, nextChapter, currentIndex, segmentTimes]);

  const isPrevDisabled = useMemo(() => {
    if (isFullClip && !splitsEnabled) {
      return currentChapterIndex === undefined || currentChapterIndex <= 0;
    }
    return currentIndex <= 0;
  }, [isFullClip, splitsEnabled, currentChapterIndex, currentIndex]);

  return {
    onClickNext,
    onClickPrev,
    isNextDisabled,
    isPrevDisabled,
    isFullClip,
    splitsEnabled
  };
};
